import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import type { ApiResponse, ApiError } from '../types/api';
import type { RefreshTokenResponse } from '../types/auth';

// 从环境变量获取API地址，支持Web和Mobile
const getApiBaseUrl = (): string => {
  // Mobile环境 (Expo/React Native) - 使用 Constants.expoConfig 或 EXPO_PUBLIC_API_URL
  try {
    // 先尝试 Expo 的环境变量
    const Constants = require('expo-constants').default;
    
    // Expo SDK 49+ 使用 EXPO_PUBLIC_ 前缀的环境变量
    if (Constants?.expoConfig?.extra?.apiUrl) {
      return Constants.expoConfig.extra.apiUrl;
    }
    
    // 检查 process.env (Expo 会在构建时注入 EXPO_PUBLIC_ 变量)
    if (typeof process !== 'undefined' && process.env?.EXPO_PUBLIC_API_URL) {
      return process.env.EXPO_PUBLIC_API_URL;
    }
  } catch (e) {
    // expo-constants 未安装，可能是 Web (Vite) 环境
  }
  
  // Web环境 (Vite) - 检查全局注入的环境变量
  // 注意：不使用 import.meta 因为 Metro bundler 不支持
  if (typeof window !== 'undefined') {
    // Vite 会在构建时替换 import.meta.env，但我们通过其他方式获取
    // @ts-ignore
    if (window.__VITE_API_URL__) {
      // @ts-ignore
      return window.__VITE_API_URL__;
    }
  }
  
  // 检查 process.env (Node.js 环境或构建时注入)
  if (typeof process !== 'undefined' && process.env?.VITE_API_URL) {
    return process.env.VITE_API_URL;
  }
  
  // 默认地址（使用公网映射地址方便测试）
  return 'https://api.tradingmind.app/api/v1';
};

const API_BASE_URL = getApiBaseUrl();

// Debug: 打印实际使用的 API 地址
console.log('[TraderMind API] Base URL:', API_BASE_URL);

let tokenGetter: (() => string | null) | null = null;
let refreshTokenGetter: (() => string | null) | null = null;
let tokenSetter: ((token: string) => void) | null = null;
let tokenExpiredHandler: (() => void) | null = null;
let languageGetter: (() => string) | null = null;

export const setLanguageGetter = (getter: () => string) => {
  languageGetter = getter;
};
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

export const setTokenGetter = (getter: () => string | null) => {
  tokenGetter = getter;
};

export const setRefreshTokenGetter = (getter: () => string | null) => {
  refreshTokenGetter = getter;
};

export const setTokenSetter = (setter: (token: string) => void) => {
  tokenSetter = setter;
};

export const setTokenExpiredHandler = (handler: () => void) => {
  tokenExpiredHandler = handler;
};

const processQueue = (error: Error | null, token: string | null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 自动重试配置 - 解决 iOS 模拟器冷启动网络错误
const shouldRetry = (error: AxiosError): boolean => {
  // 仅重试网络错误（ERR_NETWORK）和超时错误
  return (
    error.code === 'ERR_NETWORK' ||
    error.code === 'ECONNABORTED' ||
    !error.response // 无响应表示网络问题
  );
};

const retryDelay = (retryCount: number): number => {
  // 指数退避：500ms, 1000ms, 2000ms
  return Math.min(1000 * Math.pow(2, retryCount - 1), 2000);
};

// 添加重试拦截器
apiClient.interceptors.response.use(undefined, async (error: AxiosError) => {
  const config = error.config as InternalAxiosRequestConfig & { 
    _retry?: boolean;
    _retryCount?: number;
  };
  
  if (!config) {
    return Promise.reject(error);
  }

  // 初始化重试计数
  config._retryCount = config._retryCount || 0;
  
  // 最多重试 3 次
  const maxRetries = 3;
  
  if (config._retryCount < maxRetries && shouldRetry(error)) {
    config._retryCount += 1;
    const delay = retryDelay(config._retryCount);
    
    console.log(`[TraderMind API] Retrying request (${config._retryCount}/${maxRetries}) after ${delay}ms:`, config.url);
    
    // 等待后重试
    await new Promise(resolve => setTimeout(resolve, delay));
    return apiClient(config);
  }
  
  // 重试次数用尽或不应重试，继续原有错误处理
  return Promise.reject(error);
});

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Debug: 打印请求信息
    console.log('[TraderMind API] Request:', config.method?.toUpperCase(), `${config.baseURL || ''}${config.url || ''}`);
    
    // 添加 Authorization
    if (tokenGetter) {
      const token = tokenGetter();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    // 添加 Accept-Language 请求头
    if (languageGetter) {
      const language = languageGetter();
      config.headers['Accept-Language'] = language;
    } else {
      config.headers['Accept-Language'] = 'zh-CN';
    }
    
    return config;
  },
  (error: AxiosError) => {
    console.log('[TraderMind API] Request Error:', error.message);
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log('[TraderMind API] Response OK:', response.status);
    return response.data;
  },
  async (error: AxiosError<ApiError>) => {
    // Debug: 打印详细错误信息
    console.log('[TraderMind API] Response Error:', {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      url: error.config?.url,
    });
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = refreshTokenGetter?.();

      if (!refreshToken) {
        isRefreshing = false;
        if (tokenExpiredHandler) {
          tokenExpiredHandler();
        }
        processQueue(new Error('No refresh token'), null);
        return Promise.reject(error);
      }

      try {
        const response = await axios.post<ApiResponse<RefreshTokenResponse>>(
          `${API_BASE_URL}/auth/refresh`,
          { refresh_token: refreshToken },
          { headers: { 'Content-Type': 'application/json' } }
        );

        const newToken = response.data.data.token;

        if (tokenSetter) {
          tokenSetter(newToken);
        }

        processQueue(null, newToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }

        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(new Error('Token refresh failed'), null);
        if (tokenExpiredHandler) {
          tokenExpiredHandler();
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    const apiError: ApiError = error.response?.data || {
      success: false,
      error: {
        code: 'UNKNOWN_ERROR',
        message: error.message || 'An unknown error occurred',
      },
      timestamp: new Date().toISOString(),
    };

    return Promise.reject(apiError);
  }
);

export const createFormDataRequest = async <T>(
  url: string,
  formData: FormData,
  onProgress?: (progress: number) => void
): Promise<ApiResponse<T>> => {
  return apiClient.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(progress);
      }
    },
  });
};
